import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  mapIsoStringtoUtcObject,
  mapRepliconDateToUtcObject
} from '~/modules/common/dates/convert';
import {
  useResourceAllocationDragIndicators,
  usePeriodsByScale
} from '~/modules/resourcing/common/hooks';
import {
  TimeOffSummaryBlocks,
  HolidaySummaryBlocks
} from '~/modules/resourcing/common/components';
import { compareDateObject } from '~/modules/common/dates/compare';
import { useQuickAllocationContext } from '~/modules/quickAllocation/components/QuickAllocationUserChart/hooks';

import QuickAllocationUserProjectSummaryBlock from './QuickAllocationUserProjectSummaryBlock';
import useStyles from './useStyles';

const QuickAllocationUserProjectSummaryBlocks = ({
  resourceAllocatedHoursByDateRange,
  resourceAllocation,
  onAllocationChange,
  timeoffSummaryPeriods,
  holidaySummaryPeriods,
  overlayPeriods,
  handleAllocationPeriodClick
}) => {
  const classes = useStyles();

  const {
    chartStartDate,
    scale,
    dateRange: chartDisplayDateRange
  } = useQuickAllocationContext();

  const {
    gestureBindEvents,
    dragDelta,
    currentResizeDirection,
    dragIndicatorsInChart,
    dates
  } = useResourceAllocationDragIndicators({
    resourceAllocation,
    chartStartDate,
    scale,
    onAllocationChange,
    chartDisplayDateRange
  });

  const { displayPeriods } = usePeriodsByScale({
    rangeStart:
      resourceAllocation && dragIndicatorsInChart.startDragIndicator
        ? mapIsoStringtoUtcObject(resourceAllocation.startDate)
        : !dragIndicatorsInChart.allocationOutOfRange && chartDisplayDateRange
        ? chartDisplayDateRange.startDate
        : null,
    rangeEnd:
      resourceAllocation && dragIndicatorsInChart.endDragIndicator
        ? mapIsoStringtoUtcObject(resourceAllocation.endDate)
        : !dragIndicatorsInChart.allocationOutOfRange && chartDisplayDateRange
        ? chartDisplayDateRange.endDate
        : null,
    scale
  });

  return (
    <div className={classes.timeBlocks}>
      {displayPeriods.map((entry, index) => {
        const dateRangeIndex = resourceAllocatedHoursByDateRange.findIndex(
          e =>
            compareDateObject(
              mapIsoStringtoUtcObject(e.dateRange.startDate2),
              entry.start
            ) === 0
        );

        const startDate =
          index === 0 && dragIndicatorsInChart.startDragIndicator
            ? mapIsoStringtoUtcObject(resourceAllocation.startDate)
            : entry.start;

        const endDate =
          index === displayPeriods.length - 1 &&
          dragIndicatorsInChart.endDragIndicator
            ? mapIsoStringtoUtcObject(resourceAllocation.endDate)
            : entry.end;

        const scheduledHours = (
          resourceAllocation.user.scheduleDurationByDay || []
        )
          .filter(
            day =>
              mapRepliconDateToUtcObject(day.date) >= startDate &&
              mapRepliconDateToUtcObject(day.date) <= endDate
          )
          .reduce((acc, day) => acc + day.hours, 0);

        return (
          <Fragment key={entry.key}>
            <QuickAllocationUserProjectSummaryBlock
              startDate={startDate}
              endDate={endDate}
              allocatedHours={
                dateRangeIndex > -1
                  ? resourceAllocatedHoursByDateRange[dateRangeIndex]
                      .resourceAllocatedHours
                  : 0
              }
              resourceAllocation={resourceAllocation}
              scheduledHours={scheduledHours}
              overlayPeriods={overlayPeriods}
              handleAllocationPeriodClick={handleAllocationPeriodClick}
              gestureBindEvents={gestureBindEvents}
              dragDelta={dragDelta}
              resizeDirection={currentResizeDirection}
              firstPeriod={
                index === 0 && dragIndicatorsInChart.startDragIndicator
              }
              lastPeriod={
                index === displayPeriods.length - 1 &&
                dragIndicatorsInChart.endDragIndicator
              }
              dates={dates}
            />
          </Fragment>
        );
      })}
      <HolidaySummaryBlocks
        scale={scale}
        chartStartDate={chartStartDate}
        holidaySummaryPeriods={holidaySummaryPeriods}
        chartDisplayDateRange={chartDisplayDateRange}
        showTooltip={false}
      />
      <TimeOffSummaryBlocks
        scale={scale}
        chartStartDate={chartStartDate}
        timeoffSummaryPeriods={timeoffSummaryPeriods}
        chartDisplayDateRange={chartDisplayDateRange}
        showTooltip={false}
      />
    </div>
  );
};

QuickAllocationUserProjectSummaryBlocks.propTypes = {
  resourceAllocatedHoursByDateRange: PropTypes.array,
  resourceAllocation: PropTypes.object,
  onAllocationChange: PropTypes.func,
  timeoffSummaryPeriods: PropTypes.array,
  holidaySummaryPeriods: PropTypes.array,
  overlayPeriods: PropTypes.array,
  handleAllocationPeriodClick: PropTypes.func
};

export default QuickAllocationUserProjectSummaryBlocks;
